<template>
<div class="input-text-size"
     @focusin="$event => $event.stopPropagation && $event.stopPropagation()"
     @focusout="$event => $event.stopPropagation && $event.stopPropagation()">
 <v-row dense>
          <v-col cols="4">
            <span>Tax Year:</span>
          </v-col>
          <v-col cols="6">
            <CommonDropDowns
            :isItTaxYearWithoutAllYears=true
            @change="dataFromCommonDropDowns"
            :taxYearValue="editedItem.taxYear"
            taxYearLabelOverride=" "
             class=" mr-2 mt-n6"/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="4">
            <span>Tax ID:</span>
          </v-col>
          <v-col cols="6">
            <v-text-field
              class="mt-n3"
              dense
              hide-details
              v-model.number="editedItem.taxId"
              @change="debouncedTaxInquiry"
              @blur="debouncedTaxInquiry"
              @focus="$event => $event.target && $event.target.select()"
              @click="$event => $event.stopPropagation && $event.stopPropagation()"
              :autofocus="false"
              tabindex="-1"
            ></v-text-field>
          </v-col>
        </v-row>
</div>

</template>
<script>
import '../../../../assets/style.css';
import CommonDropDowns from '../../common-utils/CommonDropDowns.mixin.vue';
import { penaltyCalculation } from '../../common-utils/tax-common.util';
import { toShortDate } from '../../../../util/shared/tmc-global';
import { TaxWarrantsDialogMixin } from '../utils/taxWarrants.mixin';
import {
  createTaxWarrantDetailsGQLFields, warrantAmountUtils, TAX_WARRANTS_BASE_CRUD_KEY,
} from '../utils/taxWarrants.util';
import { SEARCH_DEBOUNCE_TIMER } from '../../common-utils/constants';

export default {
  name: 'TaxWarantDetailsEditedItem',
  mixins: [TaxWarrantsDialogMixin],
  data() {
    return {
      errorMessage: null,
      taxInquiryTimeout: null,
      isLoadingTaxInquiry: false,
    };
  },
  components: {
    CommonDropDowns,
  },
  props: {
    baseCrudKey: {
      type: String,
      default: '',
    },
  },
  created() {
    this.editedItem.taxYear = this.latestTaxYear;
    // Load tax config to ensure taxSettingItem is available
    this.loadConfig({ requiredScope: 'taxConfig' });
  },
  methods: {
    // Debounced version of tax inquiry to prevent rapid calls
    debouncedTaxInquiry() {
      if (this.taxInquiryTimeout) {
        clearTimeout(this.taxInquiryTimeout);
      }
      this.taxInquiryTimeout = setTimeout(() => {
        this.handleTaxInquiry();
      }, SEARCH_DEBOUNCE_TIMER);
    },

    // Override the handleTaxInquiry method from the mixin
    async handleTaxInquiry() {
      // Prevent multiple simultaneous calls
      if (this.isLoadingTaxInquiry) {
        return;
      }

      this.errorMessage = null;

      if (!this.editedItem.taxYear || !this.editedItem.taxId) return;

      this.isLoadingTaxInquiry = true;

      try {
        const gqlField = createTaxWarrantDetailsGQLFields();
        const keys = ['baseId', 'taxYear', 'taxId', 'name1', 'name2', 'totalDue', 'totalFee', 'totalTax'];

        const criteria = { taxYear: this.editedItem.taxYear, taxId: this.editedItem.taxId };
        const data = await this.loadTaxInquires({ criteria, baseCrudKey: 'taxInquiryDataForTaxWarrantDetails', fields: gqlField });
        if (data && data.length > 0) {
          const [item] = (data || []);
          keys.forEach((key) => {
            if (item[key] !== undefined && item[key] !== null) {
              if (key === 'totalTax' || key === 'totalDue' || key === 'totalFee') {
                this.editedItem[key] = warrantAmountUtils.toNumber(item[key]);
              } else {
                this.editedItem[key] = item[key];
              }
            }
          });
          // eslint-disable-next-line max-len
          // this.editedItem.penaltyAmt = warrantAmountUtils.toNumber(item.feesAndPen) - warrantAmountUtils.toNumber(item.totalFee);
          // Preview against the warrant's own chosen Penalty Date, not today - the backend
          // recalculates authoritatively from that same date on save regardless, but the
          // preview shown here should match what will actually be persisted.
          const warrantItem = this.item(TAX_WARRANTS_BASE_CRUD_KEY) || {};
          const penDate = warrantItem.penaltyDate || toShortDate();

          // Safety check for taxSettingItem
          if (!this.taxSettingItem) {
            this.flashWarn('Tax settings not loaded. Please try again.');
            return;
          }

          const { penaltyDate, penaltyRate } = this.taxSettingItem;

          // Additional safety check for required properties
          if (!penaltyDate || penaltyRate === undefined) {
            this.flashWarn('Tax penalty settings are incomplete. Please check system configuration.');
            return;
          }
          const {
            penaltyStartDate, totalTax, totalPaid, totalFee, totalDue, taxYear,
          } = item;

          const penaltyResults = penaltyCalculation(
            penDate, penaltyStartDate, penaltyDate, totalTax,
            totalPaid, totalFee, penaltyRate, totalDue, taxYear,
            [], this.taxSettingItem,
          );

          this.editedItem.penaltyAmt = penaltyResults.penaltyAmt;
          this.editedItem.totalDueWithFeesPenalty = warrantAmountUtils.toNumber(
            (item.totalDue || 0) + (penaltyResults.penaltyWithOrWithoutFees || 0),
          );
        } else {
          keys.forEach((key) => {
            if (key === 'totalTax' || key === 'totalDue' || key === 'penaltyAmt' || key === 'totalFee') {
              this.editedItem[key] = 0;
            } else {
              this.editedItem[key] = null;
            }
          });
          this.editedItem.penaltyAmt = 0;
          this.flashWarn('No tax record found!');
        }
      } catch (error) {
        console.error('Error in handleTaxInquiry:', error);
        this.flashWarn('Error loading tax inquiry data. Please try again.');
      } finally {
        this.isLoadingTaxInquiry = false;
      }
    },

    async dataFromCommonDropDowns(val, whichField) {
      this.editedItem[whichField] = val;
      await this.debouncedTaxInquiry();
    },

    // Method to ensure tax inquiry data is loaded before saving
    async ensureTaxInquiryLoaded() {
      if (!this.editedItem.taxYear || !this.editedItem.taxId) {
        return false;
      }

      // Check if essential tax inquiry data is already loaded
      if (this.editedItem.baseId && this.editedItem.name1) {
        return true;
      }

      // If data is not loaded, try to load it
      try {
        await this.handleTaxInquiry();
        // Check again if data was loaded successfully
        return !!(this.editedItem.baseId && this.editedItem.name1);
      } catch (error) {
        console.error('Error loading tax inquiry data:', error);
        return false;
      }
    },
  },

  beforeDestroy() {
    // Clean up any pending timeouts and reset loading flag
    if (this.taxInquiryTimeout) {
      clearTimeout(this.taxInquiryTimeout);
    }
    this.isLoadingTaxInquiry = false;
  },
};
</script>
